import { tint } from 'polished';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Colors } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { Shadows } from '../../../ui/Standards';
import { Copy, Flexer, Title } from '../elements/LayoutElements';
import { useOnCreateBoardFromTemplate, useTemplateData } from './TemplateHooks';
import { TemplateCard } from './gallery/TemplateCard';

const useTemplatesByAliasOrId = (idsOrAliases: string[]) => {
    const { templates } = useTemplateData();
    return useMemo(
        () => templates?.filter(x => idsOrAliases.includes(x.id) || idsOrAliases.includes(x.alias)) ?? [],
        [templates, idsOrAliases],
    );
};

const Frame = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    max-width: 64rem;
    height: 18rem;
    margin: 0 auto;
    overflow: hidden;
    box-shadow: 0rem 0.25rem 1.5rem 0rem rgba(59, 101, 239, 0.10);
    border-radius: .75rem;
    background: ${Colors.white};
    user-select: none;

    &:hover {
        cursor: pointer;
        box-shadow: 
            0rem 0.25rem 1.5rem 0rem rgba(59, 101, 239, 0.15),
            0rem 0rem 0rem .125rem rgba(59, 101, 239, 0.10);
    }

    &:active {
        box-shadow: 
            0rem 0.25rem 1.5rem 0rem rgba(59, 101, 239, 0.15),
            0rem 0rem 0rem .125rem rgba(59, 101, 239, 0.20);
    }
`;

const Hero = styled.div<{ color?: string }>`
    position: absolute;
    left: 11rem;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 32rem;
    height: 32rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8rem;
    background: ${p => tint(.93, p.color || Colors.primary)};
    border-radius: 50%;

    &:before {
        content: '';
        position: absolute;
        left: -5rem;
        top: 0;
        width: 100%;
        height: 100%;
        background: ${p => p.color || Colors.primary};
        border-radius: 50%;
    }
`;

const Thumb = styled.div<{ src: string }>`
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: .5rem;
    box-shadow: ${Shadows.lg};
    background: url(${p => p.src});
    background-size: cover;
    background-position: center;
`;

const ThumbMount123 = styled.div`
    position: relative;
    width: 0;
    height: 0;

    ${Thumb} {
        width: 10rem;
        height: 14rem;
    }

    &:nth-child(1) {
        transform: scale(.8);
    }
    &:nth-child(2) {
        transform: scale(.9);
    }
`;

const ThumbMountSingle = styled.div`
    position: relative;
    width: 0;
    height: 0;

    ${Thumb} {
        width: 26rem;
        height: 18rem;
        margin-left: 1rem;
    }

    &:nth-child(1) {
        transform: scale(.8);
    }
    &:nth-child(2) {
        transform: scale(.9);
    }
`;

const TemplateSequence = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: start;
`;

const TemplateSequenceItem = styled.div`
    flex: 0 0 auto;
    display: flex;
    flex-direction: row;
    align-items: center;

    svg {
        margin: 0 1rem;
        color: ${Colors.textLighter};
    }
`;

const TemplateLabel = styled.div`

    h2 {
        color: ${Colors.textDark};
        font-size: 1rem;
        font-weight: 500;
    }
    h4 {
        font-size: .75rem;
        font-weight: 500;
    }

    &:nth-child(1) {
        h4 { color: ${Colors.positive}; }
    }
    &:nth-child(2) {
        h4 { color: ${Colors.primary}; }
    }
    &:nth-child(3) {
        h4 { color: ${Colors.mystery}; }
    }
    &:nth-child(4) {
        h4 { color: ${Colors.observe}; }
    }
`;

const Main = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    padding: 2rem 4rem;
    margin-left: 26rem;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;

    ${Badge} {
        display: inline-block;
        margin: 0 0 .75em;
        padding: 0;
        font-weight: 500;
        font-size: .75rem;
        color: ${Colors.textLighter};
        background: none;
        text-transform: uppercase;
    }

    ${Title} {
        margin: 0 0 .5em;
        line-height: 1.25em;
        font-size: 1.375rem;
        font-weight: 500;
        color: ${Colors.text};
    }
    ${Copy} {
        margin: 0;
        line-height: 1.25em;
        font-size: 1;
        font-weight: 400;
        color: ${Colors.textLight};
    }
`;

const Callout = styled.div`
    flex: 0 0 auto;
    background: ${p => p.color};
    border-radius: .75rem 0 0 .75rem;
    font-family: "Caveat";
    font-size: 2rem;
    font-weight: 500;
    color: ${Colors.primaryLight};
`;

export type TemplateSpotlightSingleProps = {
    templateId: string,
};

export const TemplateSpotlightSingle = React.memo<TemplateSpotlightSingleProps>(({ templateId }) => {

    const [template] = useTemplatesByAliasOrId([templateId]);
    const { color, copy } = template.metadata;

    const onCreate = useOnCreateBoardFromTemplate();

    return <Frame onClick={() => onCreate({ templateId: template.id, autoOpen: true, forcePromptForLocation: true })}>
        <Hero color={color || Colors.primary}>
            <ThumbMountSingle>
                <Thumb src={template.thumbUrl} />
            </ThumbMountSingle>
        </Hero>
        <Main>
            <Body>
                <Flexer shrink>
                    <Title>{template.label}</Title>
                    <Copy>
                        {copy?.map((x, i) => <p key={i}>{x}</p>)}
                    </Copy>
                </Flexer>
            </Body>
            <Flexer flex="row" align="center" justify="space-between">
                <TemplateCard.TagList>
                    {template.tags.map(x => <TemplateCard.Tag key={x.id} color={x.color}>{x.label}</TemplateCard.Tag>)}
                </TemplateCard.TagList>
                <Callout>
                    Featured Template!
                </Callout>
            </Flexer>
        </Main>
    </Frame>;
});
